import { i18n } from "@lingui/core";
import { type AriaNumberFieldProps, useNumberField } from "@react-aria/numberfield";
import { useNumberFieldState } from "@react-stately/numberfield";
import {
  assertNever,
  clsx,
  EMPTY_STRING,
  formatNumber,
  mergeRefs,
  noop,
  type WithClassName,
} from "@regrello/core-utils";
import React, { useCallback, useRef, useState } from "react";
import { useDeepCompareEffect } from "react-use";

import type { RegrelloIntent } from "../../utils/enums/regrelloIntent";
import { RegrelloSize } from "../../utils/enums/regrelloSize";
import { RegrelloButton } from "../button/RegrelloButton";
import { RegrelloButtonGroup } from "../button/RegrelloButtonGroup";
import { INPUT_V2_DEFAULT_SIZE } from "../input/inputV2Utils";
import { RegrelloInput, type RegrelloInputProps } from "../input/RegrelloInput";

export interface RegrelloNumericInputProps
  extends Pick<RegrelloInputProps, "name" | "onKeyDownCapture" | "startElement">,
    Omit<
      AriaNumberFieldProps,
      | "defaultValue"
      | "description"
      | "errorMessage"
      | "isDisabled"
      | "isInvalid"
      | "isReadOnly"
      | "isRequired"
      | "label"
      | "onChange"
      | "validationState"
      | "value"
    >,
    WithClassName {
  /**
   * The initial value to show in the input in uncontrolled mode.
   *
   * ___Note:___ __This component _must_ be used in uncontrolled mode in Regrello!__ Due to the
   * underlying library we use to manage numeric-input state, a value inputted via keyboard will be
   * emitted only on blur or on increment; this means that autosaving the value and then typing some
   * more will typically result in the newly inputted value being "clobbered" when the controlled
   * autosaved value is passed back down from the parent. To avoid this, we pass in the initial
   * value as `defaultValue` and then use the `onChange` prop to listen for changes.
   */
  defaultValue?: number | null;

  /** Whether the input is non-interactive. */
  disabled?: boolean;

  /** A ref to the input element. */
  inputRef?: React.Ref<HTMLInputElement>;

  /** A semantic intent to apply to this */
  intent?: Extract<RegrelloIntent, "neutral" | "warning" | "danger">;

  /**
   * A custom locale to use for this numeric input, if needed. Only provide this if you do not want
   * to use the default `i18n.locale` from Lingui.
   */
  locale?: string;

  /** The callback to call when the input value changes. */
  onChange?: (value: number | null) => void;

  /** Whether the input value can be selected but not changed by the user. */
  readOnly?: boolean;

  /** Whether value is required on this input in order for the parent form to be submitted.  */
  required?: boolean;

  /**
   * The size of this input.
   *
   * @default "large"
   */
  size?: RegrelloSize;
}

/**
 * A form input that allows the user to enter a number, and increment or decrement the value using
 * stepper buttons.
 */
export const RegrelloNumericInput = React.memo<RegrelloNumericInputProps>(function RegrelloNumericInputFn({
  className,
  defaultValue,
  disabled: isDisabled = false,
  formatOptions,
  inputRef: propsInputRef,
  intent,
  locale = i18n.locale,
  maxValue,
  minValue,
  name,
  onBlur,
  onChange: propsOnChange,
  readOnly: isReadOnly = false,
  size = INPUT_V2_DEFAULT_SIZE,
  startElement,
  step,
  ...restProps
}) {
  const handleChange = useCallback(
    // Maps from a `number` in this scope to a `number | null` in the parent scope.
    (value: number) => {
      if (Number.isNaN(value)) {
        propsOnChange?.(null);
      } else {
        propsOnChange?.(value);
      }
    },
    [propsOnChange],
  );

  const memoizedFormatOptions = useMemoizedFormatOptions(formatOptions);

  // HACKHACK (clewis): More silliness here to avoid controlled/uncontrolled value clobbering: we
  // cannot spread ...restProps here, because that risks triggering a re-render of the input state
  // if e.g. we pass in an unmemoized callback or something, which would clobber the uncontrolled
  // value with the last-saved controlled value.
  //
  // Thus, we specify only a few props here that we know we want to listen for, and we take care to
  // memoize *all* of them!
  const state = useNumberFieldState({
    // Only affects initial render, and is a primitive type:
    defaultValue: defaultValue === null ? Number.NaN : defaultValue,

    // Memoized via being a primitive type:
    isDisabled,

    // Memoized via being a primitive type:
    isReadOnly,

    // Memoized via being a primitive type:
    label: null,

    // Memoized via being a primitive type:
    locale,

    // Memoized via useCallback:
    onChange: handleChange,

    // Memoized via useMemoizedFormatOptions:
    formatOptions: memoizedFormatOptions,

    // Memoized via being a primitive type:
    maxValue,

    // Memoized via being a primitive type:
    minValue,

    // Memoized via being a primitive type:
    step,
  });

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { groupProps, inputProps, incrementButtonProps, decrementButtonProps } = useNumberField(
    {
      ...restProps,
      onChange: handleChange,
      isDisabled,
      isReadOnly,
      label: null,
    },
    state,
    inputRef,
  );

  const { size: _incompatibleSize, type: _incompatibleType, ...spreadableInputProps } = inputProps;
  const {
    isDisabled: _incompatiableIncrementIsDisabled,
    excludeFromTabOrder: _incompatiableIncrementExcludeFromTabOrder,
    onPressStart: _incompatiableIncrementOnPressStart,
    onPressEnd: _incompatiableIncrementOnPressEnd,
    ...spreadableIncrementButtonProps
  } = incrementButtonProps;
  const {
    isDisabled: _incompatiableDecrementIsDisabled,
    excludeFromTabOrder: _incompatiableDecrementExcludeFromTabOrder,
    onPressStart: _incompatiableDecrementOnPressStart,
    onPressEnd: _incompatiableDecrementOnPressEnd,
    ...spreadableDecrementButtonProps
  } = decrementButtonProps;

  const onIncrementButtonMouseDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      incrementButtonProps.onPressStart?.(convertMouseOrKeyEventToPressStartEvent(event));
      // (clewis): Push to the end of the event queue to:
      // 1. Ensure the input is focused even if the stepper button becomes disabled from reaching
      //    the `maxValue`.
      // 2. Ensure the input stays focused without visual flickering.
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [incrementButtonProps],
  );

  const onIncrementButtonMouseUp = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      incrementButtonProps.onPressEnd?.(convertMouseOrKeyEventToPressEndEvent(event));
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [incrementButtonProps],
  );

  const onIncrementButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      incrementButtonProps.onPress?.(convertMouseEventToPressEvent(event));
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [incrementButtonProps],
  );

  const onDecrementButtonMouseDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      decrementButtonProps.onPressStart?.(convertMouseOrKeyEventToPressStartEvent(event));
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [decrementButtonProps],
  );

  const onDecrementButtonMouseUp = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      decrementButtonProps.onPressEnd?.(convertMouseOrKeyEventToPressEndEvent(event));
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [decrementButtonProps],
  );

  const onDecrementButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      decrementButtonProps.onPress?.(convertMouseEventToPressEvent(event));
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [decrementButtonProps],
  );

  const inputPropsOnBlur = inputProps.onBlur;
  const handleBlur: React.FocusEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const value = event.currentTarget.value;
      if (value.length === 0) {
        propsOnChange?.(null);
      }
      inputPropsOnBlur?.(event);
      onBlur?.(event);
    },
    [inputPropsOnBlur, propsOnChange, onBlur],
  );

  const handlePaste: React.ClipboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      const value = event.clipboardData.getData("text");
      if (value.length === 0) {
        propsOnChange?.(null);
        return;
      }

      if (formatOptions?.maximumFractionDigits === 0) {
        // (clewis): Needs special handling, as:
        // - Paste doesn't seem to work when pasting a decimal number into an integer-only field.
        // - We only support pasting US-style numbers (e.g., 1,234,567.89)
        //
        // We could consider introducing some heuristics to determine if the number is invalid vs.
        // in a different locale format (e.g., multiple period- or space-delimeted chunks,
        // optionally followed by a comma then more digits).
        const valueWithoutThousandsSeparators = value.replaceAll(",", "");
        const valueWithCorrectDecimalPlaces = formatNumber(Number(valueWithoutThousandsSeparators), locale, {
          decimalPlaces: formatOptions.maximumFractionDigits,
        });
        const valueWithCorrectDecimalPlacesButNoThousandsSeparators = valueWithCorrectDecimalPlaces.replaceAll(",", "");
        const valueWithCorrectDecimalPlacesButNoThousandsSeparatorsAsNumber = Number(
          valueWithCorrectDecimalPlacesButNoThousandsSeparators,
        );
        if (Number.isFinite(valueWithCorrectDecimalPlacesButNoThousandsSeparatorsAsNumber)) {
          propsOnChange?.(valueWithCorrectDecimalPlacesButNoThousandsSeparatorsAsNumber);
        }
        return;
      }

      // Must do this check after the special handling above, else pasting values from other locales
      // doesn't happen best-effort (we'd just resolve to null).
      const valueAsNumber = Number(value);
      if (Number.isNaN(valueAsNumber)) {
        propsOnChange?.(null);
        return;
      }

      propsOnChange?.(valueAsNumber);
    },
    [formatOptions?.maximumFractionDigits, locale, propsOnChange],
  );

  const buttonClassName = clsx({
    "w-4 h-4": size === "x-small",
    "w-5 h-5": size === "small",
  });

  return (
    <div aria-label={EMPTY_STRING} className="flex" {...groupProps}>
      <RegrelloInput
        {...spreadableInputProps}
        aria-label={EMPTY_STRING}
        className={clsx(spreadableInputProps.className, className)}
        endElement={{
          type: "interactive",
          element: (
            <RegrelloButtonGroup variant="ghost">
              <RegrelloButton
                {...spreadableDecrementButtonProps}
                aria-label="Decrement"
                className={buttonClassName}
                disabled={decrementButtonProps.isDisabled}
                iconOnly={true}
                onClick={onDecrementButtonClick}
                onMouseDown={onDecrementButtonMouseDown}
                onMouseLeave={onDecrementButtonMouseUp} // Stop decrementing when the mouse leaves the button.
                onMouseUp={onDecrementButtonMouseUp}
                size={getButtonSizeForInputSize(size)}
                startIcon="subtract"
                tabIndex={-1} // (clewis): Make non-focusable; only the input should be focusable.
                variant="ghost"
              />
              <RegrelloButton
                {...spreadableIncrementButtonProps}
                aria-label="Increment"
                className={buttonClassName}
                disabled={incrementButtonProps.isDisabled}
                iconOnly={true}
                onClick={onIncrementButtonClick}
                onMouseDown={onIncrementButtonMouseDown}
                onMouseLeave={onIncrementButtonMouseUp} // Stop incrementing when the mouse leaves the button.
                onMouseUp={onIncrementButtonMouseUp}
                size={getButtonSizeForInputSize(size)}
                startIcon="add"
                tabIndex={-1} // (clewis): Make non-focusable; only the input should be focusable.
                variant="ghost"
              />
            </RegrelloButtonGroup>
          ),
        }}
        fullWidth={true}
        inputRef={mergeRefs(inputRef, propsInputRef)}
        intent={intent}
        name={name}
        onBlur={handleBlur}
        onPaste={handlePaste}
        readOnly={isReadOnly}
        size={size}
        startElement={startElement}
      />
    </div>
  );
});

function convertMouseOrKeyEventToPressStartEvent(
  event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
) {
  return {
    type: "pressstart" as const,
    pointerType: "mouse" as const,
    target: event.currentTarget,
    shiftKey: event.shiftKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    altKey: event.altKey,
    continuePropagation: noop, // (clewis): Ignore.
  };
}

function convertMouseOrKeyEventToPressEndEvent(
  event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
) {
  return {
    type: "pressend" as const,
    pointerType: "mouse" as const,
    target: event.currentTarget,
    shiftKey: event.shiftKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    altKey: event.altKey,
    continuePropagation: noop, // (clewis): Ignore.
  };
}

function convertMouseEventToPressEvent(
  event: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>,
) {
  return {
    type: "press" as const,
    pointerType: "mouse" as const,
    target: event.currentTarget,
    shiftKey: event.shiftKey,
    ctrlKey: event.ctrlKey,
    metaKey: event.metaKey,
    altKey: event.altKey,
    continuePropagation: noop, // (clewis): Ignore.
  };
}

function getButtonSizeForInputSize(size: RegrelloSize) {
  switch (size) {
    case RegrelloSize.X_SMALL:
      return RegrelloSize.X_SMALL;
    case RegrelloSize.SMALL:
      return RegrelloSize.X_SMALL;
    case RegrelloSize.MEDIUM:
      return RegrelloSize.X_SMALL;
    case RegrelloSize.LARGE:
      return RegrelloSize.SMALL;
    case RegrelloSize.X_LARGE:
      return RegrelloSize.MEDIUM;
    default:
      assertNever(size);
  }
}

/**
 * Hook that memoizes `formatOptions`, which are commonly passed in as an unmemoized object and
 * which can trigger unnecessary re-renders of useNumberFieldState otherwise. (Such re-renders are a
 * common cause of "clobbering" an uncontrolled value in the numeric input.)
 */
function useMemoizedFormatOptions(formatOptions: Intl.NumberFormatOptions | undefined) {
  const [memoizedFormatOptions, setMemoizedFormatOptions] = useState(formatOptions);

  useDeepCompareEffect(() => {
    setMemoizedFormatOptions(formatOptions);
  }, [formatOptions]);

  return memoizedFormatOptions;
}
